// Écran « Interroger » : ce qui ne dépend pas de l'affichage. Tout est du texte brut : aucun HTML, aucun Markdown,
// aucune adresse tirée de la carte ne passe par ici.
import type { ChiffreConsole, ElementPhrase, MorceauConsole, OperationConsole, ReponseConsole, SourceConsole } from '../api/types';

export const LONGUEUR_QUESTION = 500;

/**
 * Questions types (mode règles) proposées à titre d'exemple : elles remplissent le champ, sans rien poser. Ce sont, mot
 * pour mot, des exemples de `src/ekera/agent/console/regles.py` (apostrophes typographiques mises à part) : le mode règles
 * les reconnaît toutes, et un test compare cette liste à celle de la console, qui possède le vocabulaire.
 */
export const QUESTIONS_TYPES: readonly string[] = [
  'Que coûterait un arrêt de l’ERP pendant cinq jours ?',
  'Qu’est-ce qui s’arrête si l’annuaire tombe ?',
  'Jusqu’où va une attaque partie de la passerelle VPN ?',
  'Quels composants sont exposés à Internet ?',
  'Quels sont les points uniques de défaillance ?',
  'Quelles sont les pertes potentielles estimées par an et le pire scénario ?',
  'Quels sont les scénarios aux pertes potentielles estimées les plus élevées ?',
  'Quel est notre indicateur de progression NIS2 et quelles mesures sont en écart ?',
  'D’où vient l’information sur l’ERP ?',
  'Quelles pièces se contredisent ?',
];

export const LIBELLE_STATUT_REPONSE: Record<ReponseConsole['statut'], string> = {
  repondu: 'Réponse établie à partir de la carte',
  partiel: 'Réponse partielle',
  refuse: 'Réponse refusée',
};

export const LIBELLE_RAISON_REFUS: Record<string, string> = {
  hors_carte: 'La question ne porte pas sur la carte.',
  ecriture_demandee: 'La console lit la carte, elle ne la modifie pas.',
  question_invalide: 'La question n’est pas valide.',
  question_non_reconnue: 'La question n’est pas reconnue sans modèle de langage.',
  garde_fou: 'Un contrôle de la console a écarté la réponse.',
  budget: 'Le budget de la question est dépassé.',
  modele_indisponible: 'Le modèle de langage n’est pas disponible.',
};

export const LIBELLE_ORIGINE: Record<ChiffreConsole['origine'], string> = {
  moteur: 'Calculé par le moteur déterministe d’Ekera',
  carte: 'Lu dans la carte',
  question: 'Lu dans votre question',
};

export const LIBELLE_MANQUE: Record<string, string> = {
  type_absent: 'Type absent de la carte',
  objet_introuvable: 'Objet introuvable',
  ambigu: 'Nom ambigu',
  correspondance_partielle: 'Correspondance partielle',
  parametre_suppose: 'Paramètre supposé',
  attribut_non_renseigne: 'Attribut non renseigné',
  non_cite: 'Non cité',
  finances_absentes: 'Données financières absentes',
  calcul_impossible: 'Calcul impossible',
  resultats_tronques: 'Résultats tronqués',
  a_confirmer: 'À confirmer',
};

export type GenreMorceau = 'texte' | 'objet' | 'attribut' | 'lien' | 'chiffre' | 'valeur' | 'inconnu';

/** La forme d'un morceau (rien d'autre que ces six formes n'est rendu). */
export function genre(m: MorceauConsole): GenreMorceau {
  if (typeof m !== 'object' || m === null) return 'inconnu';
  if ('t' in m && typeof m.t === 'string') return 'texte';
  if ('o' in m && typeof m.o === 'string') return 'objet';
  if ('a' in m && typeof m.a === 'string') return 'attribut';
  if ('l' in m && typeof m.l === 'string') return 'lien';
  if ('c' in m && typeof m.c === 'string') return 'chiffre';
  if ('v' in m && typeof m.v === 'string') return 'valeur';
  return 'inconnu';
}

/** Une ligne de calcul ou de paramètre : du texte, jamais autre chose. */
export function ligneTexte(v: unknown): string {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  try {
    return JSON.stringify(v) ?? '';
  } catch {
    return '';
  }
}

/** Les chiffres cités par un morceau, dans l'ordre. */
export function chiffresDe(morceaux: readonly MorceauConsole[]): string[] {
  return morceaux.flatMap((m) => (genre(m) === 'chiffre' ? [(m as { c: string }).c] : []));
}

export function chiffresDePhrase(elements: readonly ElementPhrase[]): string[] {
  const ids = elements.flatMap((el) => ('e' in el ? chiffresDe(el.morceaux) : []));
  return [...new Set(ids)];
}

// ---------------------------------------------------------------- le calcul d'un chiffre

/**
 * La source « calcul » qui porte un chiffre du moteur : celle des énoncés qui le citent (fonction du moteur, paramètres et
 * hypothèses, dont « fréquences, durées d'incident et coûts de rattrapage sont des hypothèses du modèle, pas encore
 * calibrées sur des sinistres réels »). Chaque énoncé dit ses chiffres et ses sources. Un chiffre lu dans la carte ou dans
 * la question n'en a pas.
 */
export function sourceDuCalcul(
  c: ChiffreConsole,
  operations: readonly OperationConsole[],
  sources: readonly SourceConsole[],
): SourceConsole | undefined {
  if (c.origine !== 'moteur') return undefined;
  const ids = new Set(
    operations.flatMap((o) => (o.enonces ?? []).filter((e) => (e.chiffres ?? []).includes(c.id)).flatMap((e) => e.sources ?? [])),
  );
  return sources.find((s) => ids.has(s.id) && s.type === 'calcul' && s.calcul);
}

/**
 * La recette que le contrôle de la console refait pour un chiffre du moteur ou de la carte, en une ligne : fonction,
 * paramètres puis champ lu (`arret · ["erp"] · 5 · loss`). Vide pour un chiffre tiré de la question.
 */
export function recetteCalcul(c: ChiffreConsole): string {
  return (c.calcul ?? []).map(ligneTexte).join(' · ');
}

// ---------------------------------------------------------------- un refus

/**
 * Le message d'un refus, écrit par la console : sa première phrase, que l'écran met en avant, puis le reste (la liste des
 * questions types d'un refus `question_non_reconnue`, par exemple, trop longue pour être lue en gras).
 */
export function phrasesDuMessage(message: string): { premiere: string; suite: string } {
  const m = /^(.+?[.?!])\s+(\S[\s\S]*)$/.exec(message);
  return m ? { premiere: m[1] ?? message, suite: m[2] ?? '' } : { premiere: message, suite: '' };
}

/** Deux textes qui disent la même chose, à l'apostrophe, aux espaces et à la casse près. */
export function memeTexte(a: string, b: string): boolean {
  const normal = (s: string) => s.replace(/[’‘]/g, "'").replace(/\s+/g, ' ').trim().toLocaleLowerCase('fr-FR');
  return normal(a) === normal(b);
}

// ---------------------------------------------------------------- synthèse : sans repère technique (T1), un élément par ligne

const ENTETE_OPERATION = /^ \((.+)\) : $/;
/** Le code sépare par « ; » les éléments d'une ligne de synthèse, mais aussi les sujets d'une liste : texte à lui, jamais un nom ni une valeur de la carte. */
const SEPARATEUR = /^\s*;\s*/;
/**
 * Un nouvel élément s'ouvre sur une étiquette : quelques mots, le premier en minuscule, puis « : » (« hypothèse : »,
 * « manques : », « nature des fréquences : », « résolus : »). Une phrase (majuscule) ou un sujet de liste (« le passage
 * réseau de … vers … ») n'en est pas une : un « ; » qui les précède sépare les sujets d'une même liste, et ne coupe rien.
 */
const ETIQUETTE = /^[a-zàâäçéèêëîïôöûùüÿœ][^:;()«»]{1,60}\s:(?:\s|$)/;

/** Le texte avec sa première lettre en majuscule (un libellé de chiffre, écrit pour s'insérer dans une phrase). */
export function majuscule(s: string): string {
  return s ? s.charAt(0).toLocaleUpperCase('fr-FR') + s.slice(1) : s;
}

/** Une ligne de synthèse prête à lire : le libellé de son opération, puis un élément par morceau de phrase. */
export interface LigneSyntheseLue {
  titre: string | null;
  elements: MorceauConsole[][];
}

/** Le solde de parenthèses ouvertes par un texte du code (jamais par un nom de la carte, qui n'est pas du texte `t`). */
function soldeParentheses(texte: string): number {
  return (texte.match(/\(/g)?.length ?? 0) - (texte.match(/\)/g)?.length ?? 0);
}

/**
 * Une ligne de synthèse commence par « opération « op2 » (arrêt supposé) : » : l'identifiant d'opération est un
 * repère technique, utile au détail du calcul mais pas au lecteur ; on n'en garde que le libellé, qui devient le titre de
 * la ligne (« Arrêt supposé »). Le code sépare ensuite les éléments de la ligne par « ; » (critères, nombre de résultats,
 * chiffre principal, nature des chiffres, hypothèses, manques) : l'écran en fait un élément par ligne, car une ligne de
 * plusieurs centaines de caractères cache, au milieu, la nature des chiffres que la règle éditoriale veut lisible.
 * On ne coupe qu'à un « ; » suivi d'une étiquette (« hypothèse : », « nature des fréquences : »), jamais entre les sujets
 * d'une liste (les contradictions résolues, par exemple, se lisent sous leur étiquette « résolus : »), ni dans une parenthèse
 * (« (« processus » ; identifiant « processus:p-be ») » distingue deux noms qui se lisent pareil). Aucun mot n'est ajouté,
 * retiré ni déplacé, hors l'identifiant d'opération et les « ; » qui deviennent des fins de ligne. Une ligne d'une autre
 * forme (énoncé non cité) est rendue telle quelle, en un seul élément.
 */
export function decouperSynthese(morceaux: readonly MorceauConsole[]): LigneSyntheseLue {
  const [a, b, c, ...reste] = morceaux;
  if (a && b && c && genre(a) === 'texte' && (a as { t: string }).t === 'opération ' && genre(b) === 'valeur' && genre(c) === 'texte') {
    const m = ENTETE_OPERATION.exec((c as { t: string }).t);
    if (m) {
      const elements: MorceauConsole[][] = [[]];
      let ouvertes = 0; // parenthèses ouvertes dans l'élément en cours
      reste.forEach((x, i) => {
        const texte = genre(x) === 'texte' ? (x as { t: string }).t : null;
        if (texte !== null && ouvertes === 0 && SEPARATEUR.test(texte)) {
          // « ; hypothèse : » porte son étiquette ; un « ; » seul est suivi d'un texte du code, ou d'un nom ou d'un chiffre
          const suite = texte.replace(SEPARATEUR, '');
          const suivant = reste[i + 1];
          const apres = suite !== '' ? suite : suivant && genre(suivant) === 'texte' ? (suivant as { t: string }).t : '';
          if (ETIQUETTE.test(apres)) {
            elements.push(suite ? [{ t: suite }] : []);
            ouvertes = Math.max(0, soldeParentheses(suite));
            return;
          }
        }
        elements[elements.length - 1]!.push(x);
        if (texte !== null) ouvertes = Math.max(0, ouvertes + soldeParentheses(texte));
      });
      return { titre: majuscule(m[1] ?? ''), elements: elements.filter((e) => e.length > 0) };
    }
  }
  return { titre: null, elements: [[...morceaux]] };
}

/** Même règle pour le texte d'une ligne de synthèse (copie en texte). */
export function texteSynthese(texte: string): string {
  // la console met des espaces insécables autour de l'identifiant, comme autour de tout nom entre « »
  const m = /^opération\s«\s[^»]{1,40}\s»\s\(([^)]+)\)\s:\s/.exec(texte);
  return m ? `${majuscule(m[1] ?? '')} : ${texte.slice(m[0].length)}` : texte;
}

// ---------------------------------------------------------------- copie en texte

/**
 * Neutralise un texte avant de le copier dans un outil qui rend le Markdown ou le HTML (même esprit que
 * `agent/rapport.neutraliser`, O25) : un nom écrit par un tiers ne doit réveiller ni lien, ni image, ni balise.
 * Les caractères de contrôle et les marques bidirectionnelles sont retirés (un nom ne passe pas pour autre chose).
 */
export function neutraliser(s: string): string {
  return s
    .replace(/[\u0000-\u0008\u000b-\u001f\u007f-\u009f\u200b-\u200f\u202a-\u202e\u2066-\u2069\ufeff]/g, '')
    .replace(/[\\<>[\]`]/g, (c) => `\\${c}`);
}

/** La réponse en texte, telle qu'elle est montrée (noms entre « »), neutralisée. Ni la question ni les extraits. */
export function texteCopie(r: ReponseConsole): string {
  const bloc: string[] = [];
  const entete = `${LIBELLE_STATUT_REPONSE[r.statut] ?? r.statut} (carte ${r.carte}, révision ${r.revision}).`;
  bloc.push(entete);
  if (r.statut === 'refuse') bloc.push(r.message ?? LIBELLE_RAISON_REFUS[r.refus?.raison ?? ''] ?? '');
  for (const p of r.phrases) bloc.push(p.texte);
  if (r.synthese.length) bloc.push('Synthèse :', ...r.synthese.map((l) => `- ${texteSynthese(l.texte)}`));
  const manques = r.manques.filter((m) => m.texte);
  if (manques.length) bloc.push('Ce qui manque :', ...manques.map((m) => `- ${m.texte}`));
  return bloc
    .filter((l) => l !== '')
    .map(neutraliser)
    .join('\n');
}

// ---------------------------------------------------------------- historique de la session (mémoire seulement)

export interface EntreeHistorique {
  /** Numéro d'ordre dans la session (pas un identifiant du serveur). */
  n: number;
  carte: string;
  question: string;
  reponse: ReponseConsole;
}

const MAX_HISTORIQUE = 20;
let historique: EntreeHistorique[] = [];
let compteur = 0;
let version = 0;
const abonnes = new Set<() => void>();

/**
 * Historique des questions de la session : dans une variable du module, jamais dans le stockage du navigateur ni dans
 * l'adresse. Il disparaît au rechargement de la page, à la déconnexion et au changement de personne.
 */
export function lireHistorique(carte: string): EntreeHistorique[] {
  return historique.filter((e) => e.carte === carte);
}
export function ajouterHistorique(carte: string, question: string, reponse: ReponseConsole): EntreeHistorique {
  const e = { n: ++compteur, carte, question, reponse };
  historique = [e, ...historique].slice(0, MAX_HISTORIQUE);
  version++;
  abonnes.forEach((f) => f());
  return e;
}
/** Fin de session ou changement de personne (appelé avec `effacerComplements`). */
export function oublierHistorique(): void {
  historique = [];
  version++;
  abonnes.forEach((f) => f());
}
/** Numéro de version de l'historique (instantané stable pour `useSyncExternalStore`). */
export function versionHistorique(): number {
  return version;
}
export function surHistorique(f: () => void): () => void {
  abonnes.add(f);
  return () => {
    abonnes.delete(f);
  };
}
